/**
 * Logo + optional name composition for gift sets, and the set preview that
 * renders every item on one shared millimetre scale.
 */
import { createRef, useImperativeHandle, useMemo, useRef, type Ref, type RefObject } from 'react';
import type { InkSetup } from '@shared/rfq';
import { containerViewBox } from '../product/geometry';
import { getContainer } from '../product/model';
import { Stage, type StageApi } from '../product/Stage';
import { giftPlacement, type GiftItemState } from './state';

/** Draws the logo with a name line underneath; returns the drawable. */
export function composeLogo(logo: CanvasImageSource & { width: number; height: number }, name: string | null): HTMLCanvasElement | (CanvasImageSource & { width: number; height: number }) {
  if (!name) return logo;
  const W = 1600;
  const logoH = Math.round((W * 0.8 * logo.height) / logo.width);
  const lh = Math.min(logoH, 1000);
  const lw = Math.round((lh * logo.width) / logo.height);
  const textH = Math.round(Math.max(120, lh * 0.22));
  const gap = Math.round(textH * 0.35);
  const c = document.createElement('canvas');
  c.width = W;
  c.height = lh + gap + textH;
  const ctx = c.getContext('2d')!;
  ctx.drawImage(logo, (W - lw) / 2, 0, lw, lh);
  ctx.fillStyle = '#161513';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  let size = textH * 0.72;
  ctx.font = `600 ${size}px "Onest Variable", "Onest", system-ui, sans-serif`;
  while (ctx.measureText(name).width > W * 0.96 && size > 20) {
    size -= 6;
    ctx.font = `600 ${size}px "Onest Variable", "Onest", system-ui, sans-serif`;
  }
  ctx.fillText(name, W / 2, lh + gap + textH / 2);
  return c;
}

export interface SetStageApi {
  exportMockup(): Promise<Blob | null>;
}

interface Props {
  items: GiftItemState[];
  art: (CanvasImageSource & { width: number; height: number }) | null;
  scale: number;
  offset: number;
  ink: InkSetup;
  spin: boolean;
  labelFor: (i: GiftItemState) => string;
  apiRef?: Ref<SetStageApi>;
}

export function GiftSetStage({ items, art, scale, offset, ink, spin, labelFor, apiRef }: Props) {
  const refs = useRef<Record<string, RefObject<StageApi | null>>>({});
  const totalW = items.reduce((s, i) => s + containerViewBox(getContainer(i.containerId)).w, 0) || 1;
  const aspect = art ? art.width / art.height : 1;

  useImperativeHandle(
    apiRef,
    () => ({
      async exportMockup() {
        const shots: HTMLImageElement[] = [];
        for (const i of items) {
          const blob = await refs.current[i.product]?.current?.exportMockup(520);
          if (!blob) continue;
          const img = new Image();
          img.src = URL.createObjectURL(blob);
          await img.decode().catch(() => undefined);
          shots.push(img);
        }
        if (!shots.length) return null;
        const H = Math.max(...shots.map((s) => s.height));
        const out = document.createElement('canvas');
        out.width = shots.reduce((w, s) => w + s.width, 0) + 40;
        out.height = H + 40;
        const ctx = out.getContext('2d')!;
        ctx.fillStyle = '#f4f1ec';
        ctx.fillRect(0, 0, out.width, out.height);
        let x = 20;
        for (const s of shots) {
          ctx.drawImage(s, x, 20 + H - s.height);
          x += s.width;
          URL.revokeObjectURL(s.src);
        }
        return new Promise<Blob | null>((res) => out.toBlob(res, 'image/png'));
      },
    }),
    [items],
  );

  const placements = useMemo(
    () => Object.fromEntries(items.map((i) => [i.product, art ? giftPlacement(getContainer(i.containerId), aspect, scale, offset) : null])),
    [items, art, aspect, scale, offset],
  );

  return (
    <div className="set-stage">
      {items.map((i) => {
        const c = getContainer(i.containerId);
        refs.current[i.product] ??= createRef<StageApi>();
        return (
          <figure key={i.product} className="set-stage__item" style={{ width: `${(containerViewBox(c).w / totalW) * 100}%` }}>
            <Stage
              apiRef={refs.current[i.product]}
              container={c}
              material={i.material}
              ink={ink}
              image={art}
              placement={placements[i.product]}
              view="front"
              viewAngleDeg={180}
              autoRotate={spin ? 24 : 0}
              showGuides={false}
              labels={{ printBand: '', seam: '', stageLabel: labelFor(i) }}
            />
            <figcaption className="mono">
              {labelFor(i)}
              {i.qty > 1 ? ` × ${i.qty}` : ''}
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
}
